<template>
  <d-card :class="{ 'mb-3': true, 'is-deleted': productData.deleted }">
    <d-card-body class="pb-2">
      <d-row>
        <d-col cols="5">
          <div>
            <div class="d-block">
              <d-badge
                v-if="!productData.has_usage && !parseFloat(productData.usage_cm_per_item)"
                theme="danger"
                class="mb-2">
                Brak ustawionego zużycia materiału dla wybranego materiału i rozmiaru
              </d-badge>
            </div>
            {{ productName }}<br>
            <small>
              Ilość: <span class="text-primary">{{ productData.quantity }} szt.</span> |
              Kolor: <span class="text-primary">{{ productColor }}</span>
              <template v-if="productMaterial !== 'Brak'">
                | Materiał: <span class="text-primary">{{ productMaterial }}</span><br>
                <strong>Użycie materiału:</strong> <br>
                1 szt.: <span class="text-primary">{{ productData.usage_cm_per_item }} cm<sup>2</sup></span>
                <template v-if="productData.usage_cm">
                  | Wszystkie produkty: <span class="text-primary">{{ productData.usage_cm }} cm<sup>2</sup></span>
                </template>
                <template v-if="productData.usage_bolt">
                  | Zużycie belek: <span class="text-primary">{{ productData.usage_bolt }}</span>
                </template>
              </template>
            </small>
          </div>
          <small
            v-if="canEdit"
            class="d-block pt-2">
            <a
              class="text-danger"
              href="#delete"
              @click.prevent="deleteProduct">
              <i class="material-icons">delete</i>
              Usuń produkt
            </a>
          </small>
        </d-col>

        <d-col cols="7">
          <div class="d-flex text-right align-items-center">
            <d-col cols="4">
              <label
                v-if="(canEdit && ((!procedureIsAssigned && availableSizes.length > 1) || !productData.size_id)) || (productData.size_id && (procedureIsAssigned || availableSizes.length === 1))"
                class="d-flex text-left mb-1">
                <small>Rozmiar:</small>
              </label>

              <div v-if="canEdit && ((!procedureIsAssigned && availableSizes.length > 1) || (!productData.size_id && !productData.size_name))">
                <span
                  v-if="productData.size_name && !sizeEditMode"
                  class="text-primary d-block text-left">
                  {{ productSize }}
                  <small
                    @click="sizeEditMode = true;"
                    style="cursor: pointer;">
                    <i class="material-icons">edit</i>
                    Edytuj
                  </small>
                </span>

                <div
                  v-else
                  :class="{ 'form-control': true, 'is-select': true }">
                  <v-select
                    v-model="productData.size_id"
                    placeholder="Wybierz rozmiar"
                    :options="availableSizes"
                    label="name"
                    :reduce="(size) => size.id" />
                </div>
              </div>

              <div
                v-else-if="(productData.size_id || productData.size_name) && (procedureIsAssigned || availableSizes.length === 1)"
                class="text-left py-1">
                <span class="text-primary">{{ productSize }}</span>
              </div>
            </d-col>

            <d-col cols="4">
              <label
                v-if="canEdit"
                class="d-flex text-left mb-1">
                <small>Polar:</small>
              </label>

              <div
                v-if="canEdit"
                :class="{ 'form-control': true, 'is-select': true }">
                <v-select
                  v-model="productData.polar_id"
                  :reduce="(polar) => polar.id"
                  placeholder="Wybierz polar"
                  label="name"
                  :options="polars" />
              </div>

              <div v-else-if="!canEdit && productData.polar_id">
                Polar: <span class="text-primary">{{ productPolar }}</span>
              </div>
            </d-col>

            <d-col cols="4">
              <label
                v-if="canEdit"
                class="d-flex text-left mb-1">
                <small>Przeszycie:</small>
              </label>

              <div
                v-if="canEdit"
                :class="{ 'form-control': true, 'is-select': true }">
                <v-select
                  v-model="productData.seam_id"
                  :reduce="(seam) => seam.id"
                  placeholder="Wybierz przeszycie"
                  label="name"
                  :options="seams" />
              </div>

              <div v-else-if="!canEdit && productData.seam_id">
                Przeszycie: <span class="text-primary">{{ productSeam }}</span>
              </div>
            </d-col>
          </div>
        </d-col>
      </d-row>

      <d-row v-if="productData.procedure_start_date">
        <hr class="w-100">
        <p class="text-left m-0 pl-3 pt-1 w-75">
          Wybrana procedura:
          <span class="text-primary">
            {{ procedureName }}
          </span>
          <span v-if="productData.processes.length">
              |
            Wykonano {{ numberOfFinishedProcessItems }} / {{ productData.processes.length }} procesów
          </span>
        </p>
        <d-button
          class="ml-auto"
          theme="warning"
          :outline="true"
          @click.prevent="addSpecialProcedure(productData.id)">
          Dodaj procedurę awaryjną
        </d-button>
        <hr class="w-100">

        <d-alert
          v-if="this.productData.procedure_is_assigned && !productData.processes.length"
          show
          theme="warning"
          class="w-100 text-center">
          Zapisz zamówienie aby móc edytować procesy procedury dla tego produktu
        </d-alert>

        <div class="w-100 processes-list">
          <template v-for="(process, index) of productData.processes">
            <div
              :key="'process-item-wrapper-' + index"
              :class="{ 'process-item': true }">
              <d-card
                :key="'process-' + index"
                class="mb-3">
                <d-card-body class="p-3">
                  <d-badge
                    v-if="processesStates[index] === 'DONE'"
                    theme="success">
                    <i class="material-icons">check</i>
                  </d-badge>

                  <d-badge
                    v-if="processesStates[index] === 'IN_PROGRESS'"
                    theme="primary">
                    <i class="material-icons">autorenew</i>
                  </d-badge>

                  <d-badge
                    v-if="processesStates[index] === 'WAITING'"
                    theme="secondary">
                    <i class="material-icons">schedule</i>
                  </d-badge>

                  <d-badge
                    v-if="processesStates[index] === 'WAITING_REMOTE_JOB'"
                    theme="secondary">
                    <i class="material-icons">local_shipping</i>
                  </d-badge>

                  <d-row>
                    <d-col
                      :cols="process.type === 'VERIFICATION' ? 5 : 6"
                      class="d-flex align-items-center">
                      <strong class="font-weight-bold text-dark">{{ process.name }}</strong>
                    </d-col>

                    <d-col
                      v-if="process.type === 'VERIFICATION'"
                      cols="2"
                      class="d-flex">
                      <d-button
                        :theme="process.status === 'IN_PROGRESS' ? 'success' : 'danger'"
                        outline
                        class="w-100"
                        @click.prevent="toggleProcessItemState(index)"
                        :disabled="processesStates[index] === 'WAITING' || processesStates[index] === 'WAITING_REMOTE_JOB'">
                        <template v-if="process.status === 'IN_PROGRESS'">
                          Wykonano?
                        </template>
                        <template v-else>
                          Cofnij
                        </template>
                      </d-button>
                    </d-col>

                    <d-col
                      :cols="process.type === 'VERIFICATION' ? 5 : 6"
                      class="d-flex align-items-center justify-content-end flex-wrap">
                      <small
                        v-if="processesStates[index] === 'DONE' && startDates[index]"
                        class="text-secondary">
                      Ukończono: <strong>{{ startDates[index] }}</strong>
                      </small>

                      <small
                        v-if="(processesStates[index] === 'IN_PROGRESS' || processesStates[index] === 'WAITING' || processesStates[index] === 'WAITING_REMOTE_JOB') && startDates[index]"
                        :class="processesStates[index] === 'IN_PROGRESS' ? 'text-success' : 'text-primary'">
                        Planowana data startu procesu: <strong class="pr-2">{{ startDates[index] }}</strong>
                      </small>

                      <small
                        v-if="processesStates[index] === 'IN_PROGRESS' && startDates[index]"
                        :class="processesStates[index] === 'IN_PROGRESS' ? 'text-success' : 'text-primary'">
                        Planowana data ukończenia procesu:
                        <strong v-if="process.type === 'VERIFICATION'">
                          {{ startDates[index] }}
                        </strong>
                        <strong v-else>
                          <template v-if="endDates[index]">
                            {{ endDates[index] }}
                          </template>
                          <template v-else>
                            nieznany
                          </template>
                        </strong>
                      </small>

                      <small
                        v-if="!startDates[index]"
                        class="text-danger">
                        <i class="material-icons">warning</i>
                        Nie można na ten moment obliczyć dat dla tego procesu
                      </small>
                    </d-col>
                  </d-row>

                  <d-row v-if="process.type !== 'VERIFICATION' && processesStates[index] !== 'DONE'">
                    <d-col cols="6" class="d-flex mt-3 ml-auto">
                      <d-button
                        theme="success"
                        outline
                        class="w-25 ml-auto"
                        @click.prevent="assignWorker(index, process.process_id, process.start_at, productData.quantity)">
                        Wybierz pracownika
                      </d-button>
                    </d-col>
                  </d-row>

                  <d-alert
                    v-if="invalidSumOfQuantity(process.items, productData.quantity)"
                    theme="danger"
                    show
                    class="text-center my-2">
                    <strong>Uwaga!</strong> Suma sztuk do wykonania jest większa niż zaplanowana ilość sztuk produktu
                  </d-alert>

                  <d-alert
                    v-if="invalidSumOfQuantityDone(process.items, productData.quantity)"
                    theme="danger"
                    show
                    class="text-center my-2">
                    <strong>Uwaga!</strong> Ilość wykonanych sztuk jest nieprawidłowa
                  </d-alert>

                  <d-alert
                    v-if="invalidSumOfQuantityDoneInProcedure(index, productData.processes)"
                    theme="warning"
                    show
                    class="text-center my-2">
                    <strong>Uwaga!</strong> Ilość wykonanych sztuk przekracza ilość wykonanych sztuk w poprzednim procesie.
                  </d-alert>

                  <table
                    v-if="process.type !== 'VERIFICATION' && process.items.length"
                    class="table table-sm w-100 mt-3 mb-0">
                    <thead>
                      <tr>
                        <th><small><strong>Pracownik</strong></small></th>
                        <th class="text-center" style="width: 150px;"><small><strong>Do wykonania</strong></small></th>
                        <th class="text-center" style="width: 200px;"><small><strong>Plan. data ukończenia</strong></small></th>
                        <th class="text-center" :style="process.type === 'SEWING' ? 'width: 200px;' : 'width: 360px;'"><small><strong>Wykonano</strong></small></th>
                        <th
                          v-if="process.type === 'SEWING'"
                          class="text-center"
                          style="width: 160px;">
                          <small><strong>Nici</strong></small>
                        </th>
                        <th class="text-center" style="width: 160px;"><small><strong>Akcje</strong></small></th>
                      </tr>
                    </thead>

                    <tfoot>
                      <tr>
                        <td></td>
                        <td class="text-center">
                          <small>
                            Razem:
                            <strong>{{ process.items.reduce((sum, item) => sum + parseInt(item.quantity, 10), 0) }} szt.</strong>
                          </small>
                        </td>
                        <th class="text-center"></th>
                        <th class="text-center">
                          <small>
                            Razem:
                            <strong>{{ process.items.reduce((sum, item) => sum + parseInt(item.quantity_done, 10), 0) }} szt.</strong>
                          </small>
                        </th>
                        <th v-if="process.type === 'SEWING'"></th>
                        <th class="text-center"></th>
                      </tr>
                    </tfoot>

                    <tbody>
                      <template v-for="(processItem, subindex) of process.items">
                        <tr
                          v-if="processItem.is_remote_job"
                          :key="'process-item-heading-' + index + '-' + subindex">
                          <td
                            colspan="7"
                            class="text-center"
                            :style="'background: ' + ((processItem.remote_job && parseInt(processItem.quantity, 10) !== parseInt(processItem.remote_job.quantity_to_execution, 10)) ? '#c4183c' : '#17c671') + '; color: #fff; padding: 0; text-transform: uppercase; font-weight: bold;'">
                            <small>Praca zdalna</small>
                            <small v-if="processItem.remote_job && parseInt(processItem.quantity, 10) !== parseInt(processItem.remote_job.quantity_to_execution, 10)"> - Uwaga! Liczba sztuk w pracy zdalnej nie zgadza się z liczbą sztuk do wykonania</small>
                          </td>
                        </tr>
                        <tr
                          :class="{
                            'is-overdue': processItem.end_at && +processItem.end_at.split('-').join('') < currentDate && processItem.quantity_done < processItem.quantity
                          }"
                          :key="'process-item-' + index + '-' + subindex">
                          <td class="align-middle">
                            {{ getWorkerName(processItem.user_id) }}<br>
                            <a
                              href="#"
                              class="text-primary"
                              @click.prevent="showWorkerPerformance(processItem.user_id)">
                              <small>
                                <i class="material-icons">leaderboard</i>
                                Zobacz zadania pracownika
                              </small>
                            </a>

                            <template v-if="!workersPerformance[processItem.user_id]">
                              <br>
                              <small class="text-danger">
                                <i class="material-icons">warning</i>
                                Brak informacji o wydajności pracownika
                              </small>
                            </template>
                          </td>
                          <td class="align-middle">
                            <d-input-group append="szt.">
                              <d-input
                                type="number"
                                min="1"
                                :state="(errors['quantity-' + index + '-' + subindex] || invalidSumOfQuantity(process.items, productData.quantity)) ? 'invalid' : null"
                                :max="productData.quantity"
                                v-model="processItem.quantity"
                                class="text-right"
                                @input="clearError('quantity-' + index + '-' + subindex)" />
                            </d-input-group>
                          </td>
                          <td class="align-middle text-center">
                            <small v-if="processItem.end_at">
                              <span
                                v-if="processItem.end_at && +processItem.end_at.split('-').join('') < currentDate && processItem.quantity_done < processItem.quantity"
                                class="text-danger">
                                {{ processItem.end_at }}
                              </span>
                              <template v-else>
                                {{ processItem.end_at }}
                              </template>
                            </small>
                            <small
                              v-else
                              class="text-warning">
                              Zapisz zamówienie aby poznać datę ukończenia
                            </small>
                          </td>
                          <td class="align-middle">
                            <d-input-group append="szt.">
                              <d-input-group-addon prepend>
                                <d-btn
                                  theme="success"
                                  @click.prevent="processItem.quantity_done = processItem.quantity"
                                  :disabled="processesStates[index] === 'WAITING_REMOTE_JOB' || processItem.quantity_done === processItem.quantity">
                                  <i class="material-icons">check_circle</i>
                                </d-btn>
                              </d-input-group-addon>
                              <d-input
                                type="number"
                                min="1"
                                :max="productData.quantity"
                                class="text-right"
                                :disabled="processesStates[index] === 'WAITING_REMOTE_JOB'"
                                :state="(errors['quantity-done-' + index + '-' + subindex] || invalidSumOfQuantityDone(process.items, productData.quantity) || parseInt(processItem.quantity_done, 10) > parseInt(processItem.quantity, 10)) ? 'invalid' : null"
                                @input="clearError('quantity-done-' + index + '-' + subindex)"
                                v-model="processItem.quantity_done" />
                            </d-input-group>
                          </td>
                          <td
                            v-if="process.type === 'SEWING'"
                            class="align-middle">
                            <div
                              :class="{ 'form-control is-select w-100': true, 'is-invalid': errors['strands-' + index + '-' + subindex] }">
                              <v-select
                                placeholder="Wybierz nić"
                                :options="strands"
                                label="name"
                                @input="clearError('strands-' + index + '-' + subindex)"
                                v-model="processItem.strand_id"
                                :reduce="strand => strand.id" />
                            </div>
                          </td>
                          <td class="align-middle">
                            <d-button
                              theme="danger"
                              outline
                              class="w-100"
                              @click.prevent="removeProcessItem(index, subindex)">
                              Usuń
                            </d-button>
                          </td>
                        </tr>
                        <tr :key="'process-item-remote-job-' + index + '-' + subindex">
                          <td colspan="2">
                            <d-checkbox
                              inline
                              toggle-small
                              v-model="processItem.is_remote_job"
                              toggle
                              class="mt-1 mb-0"
                              :disabled="!!((processItem.remote_job && processItem.remote_job.delivered_at) || (processItem.remote_job && processItem.remote_job.collected_at))">
                              <small>Praca zdalna</small>
                            </d-checkbox>
                          </td>

                          <td :colspan="process.type === 'SEWING' ? 3 : 2">
                            <div
                              v-if="processItem.remote_job"
                              class="d-flex my-2">
                              <d-badge
                                :outline="true"
                                theme="primary"
                                class="px-2 py-0">
                                <div
                                  v-if="waitingForRemoteJobsUpdate"
                                  class="spinner-border spinner-border-sm"
                                  role="status"></div>
                                <i
                                  v-else
                                  class="material-icons">local_shipping</i>
                                Dostawa
                              </d-badge>

                              <div class="ml-3 w-100">
                                <span
                                  v-if="processItem.remote_job.delivered_at"
                                  class="text-success">
                                  {{ processItem.remote_job.quantity_to_execution }} szt. dostarczone {{ processItem.remote_job.delivered_at.split(' ')[0] }} przez <span>{{ getDriverName(processItem.remote_job.delivery_driver_user_id) }}</span>
                                </span>
                                <span
                                  v-else
                                  class="text-primary">
                                  {{ processItem.remote_job.quantity_to_execution }} szt. dostarczy {{ processItem.remote_job.planned_delivery_date }} <span>{{ getDriverName(processItem.remote_job.delivery_driver_user_id) }}</span>
                                </span>

                                <d-button
                                  v-if="!processItem.remote_job.delivered_at"
                                  size="sm"
                                  theme="success"
                                  :outline="true"
                                  class="ml-3 py-1 px-2"
                                  :disabled="!!deliverySaveInProgress"
                                  @click.prevent="markAsDelivered(processItem, processItem.remote_job.id)">
                                  <div
                                    v-if="deliverySaveInProgress === processItem.remote_job.id"
                                    class="spinner-border spinner-border-sm"
                                    role="status"></div>
                                  <i
                                    v-else
                                    class="material-icons">check</i>
                                  Oznacz jako dostarczone
                                </d-button>
                              </div>
                            </div>

                            <div
                              v-if="processItem.remote_job"
                              class="d-flex my-2">
                              <d-badge
                                :outline="true"
                                theme="success"
                                class="px-2 py-0">
                                <div
                                  v-if="waitingForRemoteJobsUpdate"
                                  class="spinner-border spinner-border-sm"
                                  role="status"></div>
                                <i
                                  v-else
                                  class="material-icons">local_shipping</i>
                                Odbiór
                              </d-badge>

                              <div class="ml-3 w-100">
                                <span
                                  v-if="processItem.remote_job.collected_at && (processItem.remote_job.quantity_to_execution === processItem.remote_job.quantity_collected)"
                                  class="text-success">
                                  {{ processItem.remote_job.quantity_to_execution }} szt. odebrane {{ processItem.remote_job.collected_at.split(' ')[0] }} przez <span>{{ getDriverName(processItem.remote_job.collection_driver_user_id) }}</span>
                                </span>
                                <span
                                  v-else-if="processItem.remote_job.collected_at && (processItem.remote_job.quantity_to_execution > processItem.remote_job.quantity_collected)"
                                  class="text-warning">
                                  {{ processItem.remote_job.quantity_collected }} z {{ processItem.remote_job.quantity_to_execution }} szt. zostało odebrane {{ processItem.remote_job.collected_at.split(' ')[0] }} przez <span>{{ getDriverName(processItem.remote_job.collection_driver_user_id) }}</span>
                                </span>
                                <span
                                  v-else
                                  class="text-primary">
                                  {{ processItem.remote_job.quantity_to_execution }} szt. ma zostać odebrane {{ processItem.remote_job.planned_collection_date }} przez <span>{{ getDriverName(processItem.remote_job.collection_driver_user_id) }}</span>
                                </span>

                                <d-button
                                  v-if="!(processItem.remote_job.collected_at && (processItem.remote_job.quantity_to_execution == processItem.remote_job.quantity_collected))"
                                  size="sm"
                                  theme="success"
                                  :outline="true"
                                  class="ml-3 px-2 py-1"
                                  :disabled="!!collectionSaveInProgress"
                                  @click.prevent="markAsCollected(processItem, processItem.remote_job.id, processItem.remote_job.quantity_to_execution - processItem.remote_job.quantity_collected)">
                                  <div
                                    v-if="collectionSaveInProgress === processItem.remote_job.id"
                                    class="spinner-border spinner-border-sm"
                                    role="status"></div>
                                  <i
                                    v-else
                                    class="material-icons">check</i>
                                  Oznacz jako odebrane
                                </d-button>
                              </div>
                            </div>
                          </td>

                          <td>
                            <d-button
                              v-if="processItem.is_remote_job && !processItem.remote_job"
                              theme="success"
                              outline
                              class="w-100"
                              @click.prevent="addRemoteJob(process, processItem, index, processItem.id)">
                              Dodaj pracę zdalną
                            </d-button>

                            <d-button
                              v-if="processItem.is_remote_job && processItem.remote_job"
                              theme="primary"
                              outline
                              class="w-100"
                              @click.prevent="editRemoteJob(process, processItem, index, processItem.id)">
                              Edytuj pracę zdalną
                            </d-button>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>

                  <d-alert
                    v-if="process.type !== 'VERIFICATION' && !process.items.length"
                    show
                    theme="light"
                    class="text-center mt-3 mb-0">
                    Wybierz pracowników, którzy zajmą się tym procesem.
                  </d-alert>
                </d-card-body>
              </d-card>
            </div>
          </template>
        </div>
      </d-row>
    </d-card-body>

    <d-card-footer
      v-if="!productData.procedure_start_date"
      bg-theme="light"
      border-theme="secondary">
      <d-row>
        <d-col cols="4">
          <d-checkbox
            inline
            :disabled="!canEdit"
            v-model="productData.pattern_required"
            toggle
            toggle-small>
            Wymaga wykroju
          </d-checkbox>

          <d-checkbox
            v-if="productData.pattern_required"
            inline
            :disabled="!canEdit"
            v-model="productData.pattern_before_order"
            toggle
            toggle-small>
            Wykrajanie przed zamówieniem
          </d-checkbox>

          <template v-if="productData.pattern_stays">
            <br>
            <small class="text-danger">
              <strong>
                Wykrój zostaje w hafciarni
              </strong>
            </small>
          </template>
        </d-col>

        <d-col cols="8" class="text-right">
          <template v-if="!procedureIsAssigned || !productData.procedure_id">
            <div
              v-if="availableProcedures.length && productData.size_id"
              class="d-flex">
              <div
                :class="{ 'form-control': true, 'is-select': true }">
                <v-select
                  v-model="productData.procedure_id"
                  placeholder="Wybierz procedurę"
                  value="value"
                  :options="availableProcedures"
                  label="name"
                  :reduce="procedure => procedure.id" />
              </div>

              <d-button
                theme="success"
                outline
                size="sm"
                class="w-50 ml-2"
                :disabled="!productData.procedure_id"
                @click.prevent="assignProcedure">
                Przypisz procedurę
              </d-button>
            </div>

            <div v-else-if="!availableProcedures.length">
              <span class="text-danger">
                Ten produkt nie ma zdefiniowanych procedur
              </span>
            </div>

            <div v-else-if="!productData.size_id">
              <span class="text-danger">
                Aby wybrać procedurę, należy najpierw wybrać rozmiar
              </span>
            </div>
          </template>
          <template v-else>
            <div class="d-flex align-items-center">
              <div class="w-50">
                Wybrana procedura:
                <span class="text-primary">
                  {{ procedureName }}
                </span>
                <small
                  class="text-danger cursor-pointer"
                  @click.prevent="changeProcedure">
                  <i class="material-icons">edit</i>
                  Zmień
                </small>
              </div>

              <div class="w-50">
                Termin startu:
                <d-button-group size="small">
                  <d-button
                    theme="success"
                    outline
                    @click.prevent="setStartDateToToday">
                    Dziś
                  </d-button>

                  <d-button
                    theme="success"
                    outline
                    @click.prevent="selectStartDate">
                    Wyznacz
                  </d-button>
                </d-button-group>
              </div>
            </div>
          </template>
        </d-col>
      </d-row>
    </d-card-footer>
  </d-card>
</template>

<script>
import Vue from 'vue';
import FormUtils from './../../../utils/FormUtils.js';

export default {
  name: 'order-creator-product',
  props: [
    'productData',
    'productIndex',
    'products',
    'procedures',
    'materials',
    'colors',
    'seams',
    'strands',
    'sizes',
    'polars',
    'workers',
    'drivers',
    'canEdit'
  ],
  computed: {
    productApiData () {
      let foundedProduct = this.products.filter(product => product.id === this.productData.product_id);

      if (foundedProduct[0]) {
        return foundedProduct[0];
      }

      return false;
    },
    productName () {
      if (this.productApiData) {
        return this.productApiData.name;
      }

      if (this.productData.product_name) {
        return this.productData.product_name;
      }

      return '---';
    },
    productsWithAllColorsAndSizes () {
      return this.products.filter(product => product.price_code === product.code).map(product => product.id);
    },
    isProductWithAllColorsAndSizes () {
      return this.productsWithAllColorsAndSizes.indexOf(this.productData.product_id) > -1;
    },
    productMaterial () {
      if (!this.productData.color_id && !this.productData.color_name) {
        return 'Brak';
      }

      if (this.productData.material_name) {
        return this.productData.material_name;
      }

      if (this.productData.color_id) {
        let foundedColor = this.colors.filter(color => color.id === this.productData.color_id);

        if (foundedColor[0]) {
          let materialID = foundedColor[0].material_id;
          let foundedMaterial = this.materials.filter(material => material.id === materialID);

          if (foundedMaterial[0]) {
            return foundedMaterial[0].name;
          }
        }
      }

      return '???';
    },
    productColor () {
      if (this.productData.color_id && this.productData.id === null) {
        let foundedColor = this.colors.filter(color => color.id === this.productData.color_id);

        if (foundedColor[0]) {
          return foundedColor[0].name;
        }

        return '???';
      }

      if (this.productData && this.productData.color_name) {
        return this.productData.color_name;
      }

      return 'Brak';
    },
    productSize () {
      if (this.productData && this.productData.size_name) {
        return this.productData.size_name;
      }

      if (this.productData.size_id) {
        let foundedSize = this.sizes.filter(size => size.id === this.productData.size_id);

        if (foundedSize[0]) {
          return foundedSize[0].name;
        }
      }

      return 'Brak';
    },
    productPolar () {
      if (this.productData && this.productData.polar_name) {
        return this.productData.polar_name;
      }

      if (this.productData.polar_id) {
        let foundedPolar = this.polars.filter(size => size.id === this.productData.polar_id);

        if (foundedPolar[0]) {
          return foundedPolar[0].name;
        }
      }

      return 'Brak';
    },
    productSeam () {
      if (this.productData && this.productData.seam_name) {
        return this.productData.seam_name;
      }

      if (this.productData.seam_id) {
        let foundedSeam = this.seams.filter(size => size.id === this.productData.seam_id);

        if (foundedSeam[0]) {
          return foundedSeam[0].name;
        }
      }

      return 'Brak';
    },
    availableSizes () {
      if (this.isProductWithAllColorsAndSizes) {
        return this.sizes.map(size => ({
          id: size.id,
          name: size.name
        }));
      }

      if (this.productApiData) {
        let sizes = JSON.parse(JSON.stringify(this.productApiData.sizes));

        if (sizes.length) {
          sizes = sizes.filter(size => !!size).map(size => ({
            id: size.id,
            name: size.name
          }));
        }

        return sizes;
      }

      return [];
    },
    availableProcedures () {
      if (this.productApiData) {
        let procedures = JSON.parse(JSON.stringify(this.productApiData.procedures));

        if (procedures.length) {
          procedures = procedures.map(procedure => ({
            id: procedure.id,
            name: procedure.name
          }));
        }

        return procedures;
      }

      return [];
    },
    procedureIsAssigned () {
      return this.productData.procedure_is_assigned || this.productData.procedure_start_date;
    },
    assignedProcedureData () {
      let procedureID = this.productData.procedure_id;
      let foundedProcedure = this.procedures.filter(procedure => procedure.id === procedureID);

      if (foundedProcedure[0]) {
        return foundedProcedure[0];
      }

      return false;
    },
    procedureName () {
      if (this.assignedProcedureData) {
        return this.assignedProcedureData.name;
      }

      return '---';
    },
    endDates () {
      return this.productData.processes.map(process => process.end_at);
    },
    startDates () {
      return this.productData.processes.map(process => process.start_at);
    },
    processesStates () {
      let states = [];
      let processes = this.productData.processes;

      for (let i = 0; i < processes.length; i++) {
        if (processes[i].type === 'VERIFICATION') {
          states[i] = processes[i].status;

          if (states[i] === 'IN_PROGRESS' && i > 0 && states[i - 1] !== 'DONE') {
            states[i] = 'WAITING';
          }
        } else {
          let quantityDone = processes[i].items.reduce((sum, item) => sum + parseInt(item.quantity_done, 10), 0);
          let isDone = quantityDone === this.productData.quantity;

          if ((i === 0 && isDone) || (isDone && states[i - 1] === 'DONE')) {
            states[i] = 'DONE';
          } else if ((!isDone && i === 0) || (!isDone && states[i - 1] === 'DONE')) {
            states[i] = 'IN_PROGRESS';
          } else {
            states[i] = 'WAITING';
          }
        }

        // Check delivery/collection
        let blockingDeliveryJobs = processes[i].delivery_remote_jobs.filter(job => job.delivery_block_process && !job.delivered_at);
        let blockingCollectionJobs = [];

        if (i > 0) {
          blockingCollectionJobs = processes[i - 1].collection_remote_jobs.filter(job => job.collection_block_process && !job.collected_at);
        }

        if ((blockingDeliveryJobs.length || blockingCollectionJobs.length) && states[i] !== 'WAITING_REMOTE_JOB') {
          states[i] = 'WAITING_REMOTE_JOB';
        }
      }

      return states;
    },
    numberOfFinishedProcessItems () {
      return this.processesStates.filter(state => state === 'DONE').length;
    },
    workersPerformance () {
      let workersPerformance = {};

      let procedureID = this.productData.procedure_id;
      let foundedProcedure = this.procedures.filter(procedure => procedure.id === procedureID);

      if (foundedProcedure[0]) {
        for (let i = 0; i < foundedProcedure[0].users.length; i++) {
          let id = foundedProcedure[0].users[i].id;
          let capability = foundedProcedure[0].users[i].capability;
          workersPerformance[id] = capability;
        }
      }

      return workersPerformance;
    },
    currentDate () {
      let currentDate = new Date();
      currentDate = [currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()].map(n => n < 10 ? '0' + n : n).join('');
      return parseInt(currentDate, 10);
    }
  },
  watch: {
    'productData.size_id': function (newValue, oldValue) {
      if (newValue !== oldValue) {
        Vue.set(this.productData, 'procedure_id', null);
      }
    }
  },
  data () {
    return {
      errors: {},
      collectionSaveInProgress: false,
      deliverySaveInProgress: false,
      waitingForRemoteJobsUpdate: false,
      sizeEditMode: false
    };
  },
  mounted () {
    this.$bus.$on('order-product-procedure-start', this.setStartDate);
    this.$bus.$on('order-product-updated-remote-jobs', this.updateRemoteJobs);
    this.$bus.$on('order-update-remote-job', this.updateRemoteJob);
    this.$bus.$on('order-product-assign-worker', this.addWorkerToProcess);
  },
  methods: {
    deleteProduct () {
      Vue.swal({
        title: 'Czy na pewno chcesz usunąć ten produkt z zamówienia?',
        text: 'Tej operacji nie można cofnąć.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.value) {
          this.$bus.$emit('order-form-remove-product', this.productIndex);
        }
      });
    },
    assignProcedure () {
      Vue.set(this.productData, 'procedure_is_assigned', true);
    },
    selectStartDate () {
      this.$bus.$emit('select-date-popup-show', {
        title: 'Wyznacz termin startu procedury',
        responseEventName: 'order-product-procedure-start',
        referrerID: this.productIndex
      });
    },
    setStartDateToToday () {
      let currentDate = new Date();
      let formattedDate = [currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-');
      this.productData.procedure_start_date = formattedDate;
    },
    setStartDate (response) {
      if (response.referrerID === this.productIndex) {
        this.productData.procedure_start_date = response.date;
      }
    },
    changeProcedure () {
      this.productData.procedure_is_assigned = false;
    },
    assignWorker (processIndex, processID, processStartDate, qty) {
      let necessaryQty = qty;
      let items = this.productData.processes[processIndex].items;
      let processName = this.productData.processes[processIndex].name;

      for (let i = 0; i < items.length; i++) {
        necessaryQty -= items[i].quantity;
      }

      this.$bus.$emit('show-order-schedule-popup', {
        productIndex: this.productIndex,
        processIndex,
        processID,
        processName,
        processStartDate,
        qty: necessaryQty
      });
    },
    addWorkerToProcess (productIndex, processIndex, userID, userQty) {
      if (this.productIndex !== productIndex) {
        return;
      }

      let productID = this.productData.product_id;
      let defaultStrandID = null;

      if (productID) {
        let foundedProduct = this.products.filter(product => product.id === productID);

        if (foundedProduct) {
          defaultStrandID = foundedProduct[0].strand_id;
        }
      }

      this.productData.processes[processIndex].items.push({
        user_id: userID,
        quantity: userQty,
        quantity_done: 0,
        strand_id: defaultStrandID,
        is_remote_job: false,
        remote_job: null
      });
    },
    toggleProcessItemState (index) {
      let status = this.productData.processes[index].status;
      this.productData.processes[index].status = status === 'DONE' ? 'IN_PROGRESS' : 'DONE';

      // Fix status of the further process items
      if (status !== 'DONE') {
        return;
      }

      // Find next processes with type of verification and change their state
      for (let i = index + 1; i < this.productData.processes.length; i++) {
        if (this.productData.processes[i].type === 'VERIFICATION' && this.productData.processes[i].status === 'DONE') {
          this.productData.processes[i].status = 'IN_PROGRESS';
        }
      }
    },
    getDriverName (userID) {
      let foundedDriver = this.drivers.filter(driver => driver.id === userID);

      if (foundedDriver[0]) {
        return foundedDriver[0].name;
      }

      return '---';
    },
    getWorkerName (userID) {
      let foundedWorker = this.workers.filter(worker => worker.id === userID);

      if (foundedWorker[0]) {
        return foundedWorker[0].name;
      }

      return '---';
    },
    removeProcessItem (processIndex, processItemIndex) {
      Vue.swal({
        title: 'Czy na pewno chcesz usunąć tego pracownika z procesu?',
        text: 'Tej operacji nie można cofnąć.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.value) {
          this.productData.processes[processIndex].items.splice(processItemIndex, 1);
        }
      });

      // uncheck further verification processes
      for (let i = processIndex; i < this.productData.processes.length; i++) {
        if (this.productData.processes[i].type === 'VERIFICATION') {
          Vue.set(this.productData.processes[i], 'status', 'IN_PROGRESS');
        }
      }
    },
    invalidSumOfQuantity (processItems, expectedQuantity) {
      let sumTodo = processItems.reduce((sum, item) => sum + parseInt(item.quantity, 10), 0);
      return sumTodo > expectedQuantity;
    },
    invalidSumOfQuantityDone (processItems, expectedQuantity) {
      let sumDone = processItems.reduce((sum, item) => sum + parseInt(item.quantity_done, 10), 0);
      let invalidItemSums = processItems.filter(item => parseInt(item.quantity_done, 10) > parseInt(item.quantity, 10));
      return sumDone > expectedQuantity || invalidItemSums.length;
    },
    invalidSumOfQuantityDoneInProcedure(index, processes) {
      let currentProcess = processes[index];
      let sumDoneInCurrentProcesss = currentProcess.items.reduce((sum, item) => sum + parseInt(item.quantity_done, 10), 0);
      let previousProcess = null;

      for (let i = 0; i < index; i++) {
        if (processes[i].type !== 'VERIFICATION') {
          previousProcess = processes[i];
        }
      }

      if (previousProcess === null) {
        return false;
      }

      let sumDoneInPreviousProcess = previousProcess.items.reduce((sum, item) => sum + parseInt(item.quantity_done, 10), 0);

      return sumDoneInCurrentProcesss > sumDoneInPreviousProcess;
    },
    setErrors (errors) {
      Vue.set(this, 'errors', errors);
    },
    clearError (key) {
      if (this.errors[key]) {
        delete this.errors[key];
      }
    },
    showWorkerPerformance (workerID) {
      let foundedWorker = this.workers.filter(worker => worker.id === workerID);

      if (foundedWorker[0]) {
        let workerPerformance = foundedWorker[0].occupancies;
        let workerName = foundedWorker[0].name;

        this.$bus.$emit('order-display-worker-performance-popup', {
          performance: workerPerformance,
          name: workerName
        });
      }
    },
    markAsDelivered (processItem, jobID) {
      this.deliverySaveInProgress = jobID;

      FormUtils.submitInBackground(this, {
        endpoint: '/orders/remote-jobs/set-delivered/' + jobID,
        errorTitle: 'Wystąpił błąd',
        errorText: 'Zapis nie powiódł się. Spróbuj ponownie',
        successAction: (response) => {
          this.deliverySaveInProgress = false;
          this.$bus.$emit('order-reload-remote-jobs');

          if (response.item) {
            processItem.remote_job = response.item;
          }
        },
        failAction: () => {
          this.deliverySaveInProgress = false;
        }
      });
    },
    markAsCollected (processItem, jobID, qty) {
      this.collectionSaveInProgress = jobID;

      FormUtils.submitInBackground(this, {
        endpoint: '/orders/remote-jobs/set-collected/' + jobID,
        params: {
          quantity_collected: qty
        },
        errorTitle: 'Wystąpił błąd',
        errorText: 'Zapis nie powiódł się. Spróbuj ponownie',
        successAction: (response) => {
          this.collectionSaveInProgress = false;
          this.$bus.$emit('order-reload-remote-jobs');

          if (response.item) {
            processItem.remote_job = response.item;
          }
        },
        failAction: () => {
          this.collectionSaveInProgress = false;
        }
      });
    },
    addRemoteJob (process, processItem, index, processItemID) {
      if (!processItemID) {
        Vue.swal({
          title: 'Zapisz zamówienie',
          text: 'Aby móc dodać pracę zdalną, należy najpierw zapisać konfigurację procesu',
          icon: 'warning',
          confirmButtonText: 'OK'
        });

        return;
      }

      this.$bus.$emit('remote-job-popup-show', {
        order_id: parseInt(this.$route.params.id, 10),
        order_product_id: this.productData.id,
        worker_user_id: processItem.user_id,
        qty: processItem.quantity,
        delivery_date: this.startDates[index],
        collection_date: processItem.end_at,
        order_process_id: process.id,
        order_process_item_id: processItemID,
        procedure_items: this.productData.processes.map(item => ({
          id: item.id,
          name: item.name
        })),
        product_qty: this.productData.quantity,
        is_new: true
      });
    },
    editRemoteJob (process, processItem, index, processItemID) {
      this.$bus.$emit('remote-job-popup-show', {
        job_id: processItem.remote_job.id,
        order_id: parseInt(this.$route.params.id, 10),
        order_product_id: this.productData.id,
        order_process_id: process.id,
        order_process_item_id: processItemID,
        procedure_items: this.productData.processes.map(item => ({
          id: item.id,
          name: item.name
        })),
        product_qty: this.productData.quantity,
        delivery_driver_user_id: processItem.remote_job.delivery_driver_user_id,
        collection_driver_user_id: processItem.remote_job.collection_driver_user_id,
        worker_user_id: processItem.remote_job.worker_user_id,
        quantity_to_execution: processItem.remote_job.quantity_to_execution,
        delivery_before_order_process_id: processItem.remote_job.delivery_before_order_process_id,
        collection_after_order_process_id: processItem.remote_job.collection_after_order_process_id,
        delivery_block_process: !!processItem.remote_job.delivery_block_process,
        collection_block_process: !!processItem.remote_job.collection_block_process,
        planned_delivery_date: processItem.remote_job.planned_delivery_date,
        planned_collection_date: processItem.remote_job.planned_collection_date,
        delivered_at: processItem.remote_job.delivered_at,
        collected_at: processItem.remote_job.collected_at
      });
    },
    updateRemoteJobs (jobItems) {
      let productJobs = jobItems.filter(job => job.order_product_id === this.productData.id);

      for (let i = 0; i < this.productData.processes.length; i++) {
        Vue.set(
          this.productData.processes[i],
          'collection_remote_jobs',
          productJobs.filter(job => job.collection_after_order_process_id === this.productData.processes[i].id)
        );
        Vue.set(
          this.productData.processes[i],
          'delivery_remote_jobs',
          productJobs.filter(job => job.delivery_before_order_process_id === this.productData.processes[i].id)
        );
      }

      this.waitingForRemoteJobsUpdate = false;
    },
    updateRemoteJob (payload) {
      for (let i = 0; i < this.productData.processes.length; i++) {
        let process = this.productData.processes[i];

        if (process.id === payload.order_process_id) {
          for (let j = 0; j < process.items.length; j++) {
            let item = process.items[j];

            if (item.id === payload.order_process_item_id) {
              item.remote_job = payload.remote_job;
            }
          }
        }
      }
    },
    addSpecialProcedure (orderProductID) {
      Vue.swal({
        title: 'Uwaga!',
        text: 'Dodanie procedury awaryjnej spowoduje odświeżenie zamówienia i utratę niezapisanych zmian. Upewnij się, że zapisano zmiany przed dodawaniem procedury awaryjnej. Czy chcesz kontynuować dodawanie procedury awaryjnej?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak, kontynuuj',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.value) {
          this.$bus.$emit('show-order-special-procedure-popup', orderProductID);
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('order-product-procedure-start', this.setStartDate);
    this.$bus.$off('order-product-updated-remote-jobs', this.updateRemoteJobs);
    this.$bus.$off('order-update-remote-job', this.updateRemoteJob);
    this.$bus.$off('order-product-assign-worker', this.addWorkerToProcess);
  }
}
</script>

<style lang="scss">
.is-deleted {
  pointer-events: none;

  & > * {
    opacity: .5;
  }

  &:before {
    background: #d22;
    border-radius: 10px 10px 0 0;
    color: #fff;
    content: "Produkt usunięty";
    height: 24px;
    font-size: 12px;
    line-height: 25px;
    position: absolute;
    text-align: center;
    width: 100%;
  }
}

.order-creator {
  .processes-list {
    .card {
      margin-left: 80px;
    }

    .card-body {
      position: relative;

      .alert {
        border-radius: 5px!important;
        font-size: 12px;
        padding: .25rem .5rem;
      }
    }

    .card-body > .badge:not(.badge-outline-primary):not(.badge-outline-success) {
      align-items: center;
      border-radius: 50%;
      display: flex;
      left: -60px;
      justify-content: center;
      height: 40px;
      padding: 0;
      position: absolute;
      top: calc(50% - 20px);
      width: 40px;

      i {
        font-size: 28px;
        position: relative;
        top: 0;
      }
    }

    .card-body > .badge.badge-outline-primary,
    .card-body > .badge.badge-outline-success {
      line-height: 1;

      .material-icons {
        margin-top: -5px;
        position: relative;
        top: 3px;
      }
    }
  }

  tr.is-overdue {
    background: rgba(255, 50, 50, .075);
  }

  .process-item {
    &.process-item-has-deliveries {
      border-radius: 10px;
      border: 3px dashed #f0f0f0;
    }
  }
}

.collection-is-loading,
.delivery-is-loading {
  opacity: .5;
}
</style>
